import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch, toImageUrl, formatDate } from "../../utils/api";

export const statusStyles = {
  Delivered: "bg-[#2f6b1f] text-white",
  Shipped: "bg-blue-600 text-white",
  Processing: "bg-amber-400 text-gray-900",
  Cancelled: "bg-red-600 text-white",
};

// short line under the order number, so customers see the state at a glance
const statusHint = {
  Processing: "We are preparing your order.",
  Shipped: "Your order is on its way.",
  Delivered: "Your order was delivered.",
  Cancelled: "This order was cancelled.",
};

const tabs = ["All Orders", "Processing", "Shipped", "Delivered", "Cancelled"];

// "All Orders" hides cancelled ones (they stay in the Cancelled tab)
const inTab = (o, tab) =>
  tab === "All Orders" ? o.status !== "Cancelled" : o.status === tab;

function OrderSkeleton() {
  return (
    <div className="mt-5 flex animate-pulse flex-col gap-3">
      {[0, 1, 2].map((n) => (
        <div key={n} className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4">
          <div className="h-16 w-24 rounded-lg bg-gray-200" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-32 rounded bg-gray-200" />
            <div className="h-3 w-48 rounded bg-gray-200" />
          </div>
          <div className="h-8 w-24 rounded bg-gray-200" />
        </div>
      ))}
    </div>
  );
}

const Orders = () => {
  const [activeTab, setActiveTab] = useState("All Orders");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    apiFetch("/orders")
      .then(({ ok, data }) => {
        if (!cancelled && ok && Array.isArray(data)) setOrders(data);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const list = orders.filter((o) => inTab(o, activeTab));

  return (
    <>
      <h1 className="text-xl font-semibold">My Orders</h1>
      <p className="mt-1 text-sm text-gray-500">
        Track, cancel or review the orders you have placed.
      </p>

      {/* ---------- tabs with counts ---------- */}
      <div
        role="tablist"
        className="-mx-1 mt-4 flex gap-2 overflow-x-auto px-1 pb-1"
      >
        {tabs.map((tab) => {
          const count = orders.filter((o) => inTab(o, tab)).length;
          const active = activeTab === tab;
          return (
            <button
              key={tab}
              role="tab"
              aria-selected={active}
              onClick={() => setActiveTab(tab)}
              className={`flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm transition ${
                active
                  ? "border-[#2f6b1f] bg-[#2f6b1f] font-medium text-white"
                  : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
              }`}
            >
              {tab}
              {!loading && (
                <span
                  className={`rounded-full px-2 py-0.5 text-[11px] ${
                    active ? "bg-white/20 text-white" : "bg-gray-100 text-gray-500"
                  }`}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ---------- list ---------- */}
      {loading ? (
        <OrderSkeleton />
      ) : list.length === 0 ? (
        <section className="mt-5 rounded-xl border border-gray-200 bg-white px-6 py-12 text-center shadow-sm">
          <p className="text-sm text-gray-500">
            {activeTab === "All Orders"
              ? "You have no orders yet."
              : `No ${activeTab.toLowerCase()} orders.`}
          </p>
          {activeTab === "All Orders" && (
            <Link
              to="/shop"
              className="mt-4 inline-block rounded-md bg-[#2f6b1f] px-5 py-2 text-sm font-medium text-white hover:bg-[#245416]"
            >
              Start shopping
            </Link>
          )}
        </section>
      ) : (
        <ul className="mt-5 flex flex-col gap-3">
          {list.map((o) => (
            <li
              key={o.id}
              className="flex flex-wrap items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:flex-nowrap"
            >
              <div className="flex h-16 w-24 shrink-0 gap-1 overflow-hidden rounded-lg bg-[#f6f6f1] p-1">
                {o.images.map((src, i) => (
                  <img
                    key={i}
                    src={toImageUrl(src)}
                    alt=""
                    onError={(e) => (e.currentTarget.style.visibility = "hidden")}
                    className="h-full w-1/2 rounded object-cover"
                  />
                ))}
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-semibold">Order #{o.orderNo}</p>
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${statusStyles[o.status]}`}
                  >
                    {o.status}
                  </span>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  {formatDate(o.createdAt)} • {o.items}{" "}
                  {o.items === 1 ? "item" : "items"}
                </p>
                <p className="mt-1 text-xs text-gray-600">{statusHint[o.status]}</p>
              </div>

              <div className="ml-auto flex shrink-0 flex-col items-end gap-2">
                <p className="text-base font-semibold">
                  ₹{o.total.toLocaleString("en-IN")}
                </p>
                <Link
                  to={`/myaccount/orders/${o.id}`}
                  className={`rounded-md px-4 py-1.5 text-center text-xs font-medium ${
                    o.status === "Shipped"
                      ? "bg-[#2f6b1f] text-white hover:bg-[#245416]"
                      : "border border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {o.status === "Shipped" ? "Track order" : "View details"}
                </Link>
              </div>
            </li>
          ))}
        </ul>
      )}
    </>
  );
};

export default Orders;
